import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Send, LogOut, Search, Users, CheckCircle2, Mail, Copy, ClipboardCheck, Inbox, Eye, X, Download, FileText, CreditCard, XCircle, Clock,
} from "lucide-react";
import { PageShell } from "@/components/site-layout";
import {
  type Application, type ApplicationStatus, type PaymentStatus, adminLoggedIn, adminLogout,
  listApplications, sendRegistrationCode, setStatus, subscribe, loadApplications, rejectApplication,
} from "@/lib/applications-store";
import { getWaitlistEntries, type WaitlistEntry } from "@/lib/api";

// Define labels locally to avoid import issues
const STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: "Pending review",
  accepted: "Accepted",
  rejected: "Rejected",
  code_sent: "Code sent",
  code_verified: "Code verified",
  registered: "Registered",
};

const PAYMENT_STATUS_LABELS: Record<"unpaid" | "paid", string> = {
  unpaid: "Unpaid",
  paid: "Paid",
};
import { adminLoggedIn as apiAdminLoggedIn } from "@/lib/api";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const FILTERS: { key: ApplicationStatus | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending review" },
  { key: "accepted", label: "Accepted" },
  { key: "rejected", label: "Rejected" },
  { key: "code_sent", label: "Code sent" },
  { key: "code_verified", label: "Code verified" },
  { key: "registered", label: "Registrations" },
];

type ViewType = "applications" | "waitlist";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [apps, setApps] = useState<Application[]>([]);
  const [filter, setFilter] = useState<ApplicationStatus | "all">("all");
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [viewType, setViewType] = useState<ViewType>("applications");
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [waitlistQuery, setWaitlistQuery] = useState("");

  useEffect(() => {
    // Check if user is authenticated using localStorage
    if (!localStorage.getItem('revlinks_admin_token')) {
      navigate({ to: "/admin/login", replace: true });
      return;
    }
    
    const refresh = async () => {
      try {
        await loadApplications();
        setApps(listApplications());
      } catch (error) {
        console.error('Failed to load applications:', error);
        // Set empty array on error to prevent crash
        setApps([]);
      }
    };
    
    // Initial load
    refresh();
    
    // Subscribe to updates, but don't call refresh immediately
    const unsubscribe = subscribe(() => {
      // Only update from cache, don't reload from API
      setApps(listApplications());
    });
    
    return unsubscribe;
  }, [navigate]);

  // Load waitlist entries when view switches to waitlist
  useEffect(() => {
    if (viewType === "waitlist") {
      const loadWaitlist = async () => {
        try {
          const entries = await getWaitlistEntries();
          setWaitlistEntries(entries);
        } catch (error) {
          console.error('Failed to load waitlist entries:', error);
          setWaitlistEntries([]);
        }
      };
      loadWaitlist();
    }
  }, [viewType]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter(
      (a) =>
        (filter === "all" || a.status === filter) &&
        (!q ||
          a.fullName.toLowerCase().includes(q) ||
          a.email.toLowerCase().includes(q) ||
          a.phone.toLowerCase().includes(q) ||
          (a.registrationCode ?? "").toLowerCase().includes(q)),
    );
  }, [apps, filter, query]);

  const filteredWaitlist = useMemo(() => {
    const q = waitlistQuery.trim().toLowerCase();
    return waitlistEntries.filter(
      (w) =>
        q === "" ||
        w.full_name.toLowerCase().includes(q) ||
        w.email.toLowerCase().includes(q) ||
        w.phone.toLowerCase().includes(q) ||
        w.location.toLowerCase().includes(q)
    );
  }, [waitlistEntries, waitlistQuery]);

  const stats = useMemo(
    () => ({
      total: apps.length,
      pending: apps.filter((a) => a.status === "pending").length,
      codes: apps.filter((a) => a.status === "code_sent").length,
      paid: apps.filter((a) => a.paymentStatus === "paid").length,
      registered: apps.filter((a) => a.status === "registered").length,
    }),
    [apps],
  );

  const onSendCode = async (id: string) => {
    const app = apps.find(a => a.id === id);
    if (!app) return;
    
    Swal.fire({
      title: 'Send Registration Code',
      text: `Send registration code to ${app.fullName}? This will accept the application and send them their registration code.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, send code',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const code = await sendRegistrationCode(id);
          if (code) {
            // TODO: Send email with registration code
            Swal.fire({
              icon: 'success',
              title: 'Registration code sent',
              text: `Code ${code} has been generated. Email sending will be implemented later.`,
              confirmButtonColor: '#3085d6',
              confirmButtonText: 'OK'
            });
          }
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to generate registration code. Please try again.',
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK'
          });
        }
      }
    });
  };

  const onReject = async (id: string) => {
    const app = apps.find(a => a.id === id);
    if (!app) return;
    
    // Prevent rejection of paid applications
    if (app.paymentStatus === 'paid') {
      Swal.fire({
        icon: 'warning',
        title: 'Cannot Reject Paid Application',
        text: 'This application has already paid for the program. Paid applications cannot be rejected.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }
    
    Swal.fire({
      title: 'Reject Application',
      text: `Reject application from ${app.fullName}? This will update their status to rejected and send them a rejection email.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, reject',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await rejectApplication(id);
          // TODO: Send rejection email
          Swal.fire({
            icon: 'success',
            title: 'Application Rejected',
            text: `Application has been rejected. Rejection email sending will be implemented later.`,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK'
          });
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to reject application. Please try again.',
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK'
          });
        }
      }
    });
  };

  return (
    <PageShell>
      <section className="border-b border-border bg-[image:var(--gradient-radial)]">
        <div className="container-x flex flex-wrap items-end justify-between gap-4 py-12">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Admin</p>
            <h1 className="mt-2 text-3xl font-bold md:text-4xl">Applications dashboard</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Review submitted applications, then send a registration code to admitted applicants.
            </p>
          </div>
          <button
            onClick={async () => { 
              await adminLogout(); 
              // Clear admin-related localStorage items
              localStorage.removeItem('revlinks_admin_id');
              localStorage.removeItem('revlinks_admin_email');
              localStorage.removeItem('revlinks_admin_name');
              localStorage.removeItem('revlinks_admin_role');
              localStorage.removeItem('revlinks_admin_token');
              navigate({ to: "/admin/login", replace: true }); 
            }}
            className="btn-ghost text-sm"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </section>

      <section className="container-x py-10">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Stat icon={Users} label="Total applications" value={stats.total} />
          <Stat icon={Inbox} label="Pending review" value={stats.pending} />
          <Stat icon={Mail} label="Codes sent" value={stats.codes} />
          <Stat icon={CreditCard} label="Paid" value={stats.paid} />
          <Stat icon={CheckCircle2} label="Registered" value={stats.registered} />
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setViewType("applications")}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                viewType === "applications"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              Applications
            </button>
            <button
              onClick={() => setViewType("waitlist")}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                viewType === "waitlist"
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              Waitlist ({waitlistEntries.length})
            </button>
          </div>
        </div>

        {viewType === "applications" && (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                      filter === f.key
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative ml-auto w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, email or code"
                  className="w-full rounded-xl border border-border bg-background py-2.5 pl-9 pr-4 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-elegant)]">
              {filtered.length === 0 ? (
                <div className="p-14 text-center">
                  <p className="text-sm text-muted-foreground">
                    No applications here yet.{" "}
                    <Link to="/apply" className="text-primary hover:underline">Submit a test application</Link> to see it appear.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-left text-sm">
                <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3.5 font-semibold">Applicant</th>
                    <th className="px-5 py-3.5 font-semibold">Contact</th>
                    <th className="px-5 py-3.5 font-semibold">Track</th>
                    <th className="px-5 py-3.5 font-semibold">Submitted</th>
                    <th className="px-5 py-3.5 font-semibold">Payment</th>
                    <th className="px-5 py-3.5 font-semibold">Status</th>
                    <th className="px-5 py-3.5 font-semibold">Code</th>
                    <th className="px-5 py-3.5 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr key={a.id} className="border-t border-border align-top transition hover:bg-muted/40">
                      <td className="px-5 py-4">
                        <p className="font-medium text-foreground">{a.fullName}</p>
                        <p className="text-xs text-muted-foreground">{a.location} · Age {a.ageRange}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-foreground">{a.email}</p>
                        <p className="text-xs text-muted-foreground">{a.phone}</p>
                      </td>
                      <td className="max-w-[220px] px-5 py-4 text-muted-foreground">{a.track}</td>
                      <td className="px-5 py-4 text-muted-foreground">
                        {new Date(a.submittedAt).toLocaleDateString(undefined, {
                          day: "2-digit", month: "short", year: "numeric",
                        })}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                          a.paymentStatus === 'paid' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {a.paymentStatus === 'paid' ? <CheckCircle2 className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
                          {PAYMENT_STATUS_LABELS[a.paymentStatus]}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                          a.status === 'accepted' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : a.status === 'rejected'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : a.status === 'code_sent'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : a.status === 'code_verified'
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                            : a.status === 'registered'
                            ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                            : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {a.status === 'accepted' && <CheckCircle2 className="h-3 w-3" />}
                          {a.status === 'rejected' && <XCircle className="h-3 w-3" />}
                          {a.status === 'code_sent' && <Mail className="h-3 w-3" />}
                          {a.status === 'code_verified' && <ClipboardCheck className="h-3 w-3" />}
                          {a.status === 'registered' && <Users className="h-3 w-3" />}
                          {STATUS_LABELS[a.status]}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs">
                        {a.registrationCode ? (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(a.registrationCode);
                              setCopied(a.id);
                              setTimeout(() => setCopied(null), 2000);
                            }}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-primary hover:bg-primary/20 transition cursor-pointer"
                            title="Click to copy code"
                          >
                            {a.registrationCode}
                            {copied === a.id ? <ClipboardCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedApp(a)}
                            title="View full details"
                            aria-label="View full details"
                            className="inline-grid h-10 w-10 place-items-center rounded-xl bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onSendCode(a.id)}
                            disabled={a.status === 'rejected' || a.status === 'registered' || a.paymentStatus === 'paid'}
                            title={a.status === 'rejected' ? "Application rejected" : a.status === 'registered' ? "Already registered" : a.paymentStatus === 'paid' ? "Already paid" : a.registrationCode ? "Resend registration code" : "Send registration code"}
                            aria-label="Send registration code"
                            className={`inline-grid h-10 w-10 place-items-center rounded-xl bg-[image:var(--gradient-primary)] text-primary-foreground shadow-[var(--shadow-elegant)] transition hover:-translate-y-0.5 ${a.status === 'rejected' || a.status === 'registered' || a.paymentStatus === 'paid' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <Send className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onReject(a.id)}
                            disabled={a.status === 'rejected' || a.status === 'registered' || a.paymentStatus === 'paid'}
                            title={a.status === 'rejected' ? "Already rejected" : (a.status === 'registered' ? "Already registered" : (a.paymentStatus === 'paid' ? "Cannot reject paid application" : "Reject application"))}
                            aria-label="Reject application"
                            className={`inline-grid h-10 w-10 place-items-center rounded-xl bg-red-500 text-white shadow-[var(--shadow-elegant)] transition hover:-translate-y-0.5 ${a.status === 'rejected' || a.status === 'registered' || a.paymentStatus === 'paid' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Applicants complete enrolment at <Link to="/register" className="text-primary hover:underline">/register</Link> using the code you issue.
        </p>
          </>
        )}

        {viewType === "waitlist" && (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="relative ml-auto w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={waitlistQuery}
                  onChange={(e) => setWaitlistQuery(e.target.value)}
                  placeholder="Search name, email, phone, or location"
                  className="w-full rounded-xl border border-border bg-background py-2.5 pl-9 pr-4 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-elegant)]">
              {filteredWaitlist.length === 0 ? (
                <div className="p-14 text-center">
                  <p className="text-sm text-muted-foreground">
                    No waitlist entries yet.{" "}
                    <Link to="/waitlist" className="text-primary hover:underline">Join the waitlist</Link> to see it appear.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-left text-sm">
                    <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-5 py-3.5 font-semibold">Name</th>
                        <th className="px-5 py-3.5 font-semibold">Contact</th>
                        <th className="px-5 py-3.5 font-semibold">Location</th>
                        <th className="px-5 py-3.5 font-semibold">Added</th>
                        <th className="px-5 py-3.5 font-semibold">Status</th>
                        <th className="px-5 py-3.5 font-semibold">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWaitlist.map((w) => (
                        <tr key={w.id} className="border-t border-border align-top transition hover:bg-muted/40">
                          <td className="px-5 py-4">
                            <p className="font-medium text-foreground">{w.full_name}</p>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-foreground">{w.email}</p>
                            <p className="text-xs text-muted-foreground">{w.phone}</p>
                          </td>
                          <td className="max-w-[220px] px-5 py-4 text-muted-foreground">{w.location}</td>
                          <td className="px-5 py-4 text-muted-foreground">
                            {new Date(w.created_at).toLocaleDateString(undefined, {
                              day: "2-digit", month: "short", year: "numeric",
                            })}
                          </td>
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                              w.status === 'active' 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                            }`}>
                              {w.status === 'active' ? <Clock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                              {w.status.charAt(0).toUpperCase() + w.status.slice(1)}
                            </span>
                          </td>
                          <td className="max-w-[300px] px-5 py-4 text-muted-foreground text-xs">
                            {w.additional_information || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {selectedApp && (
        <ApplicationDetailsModal
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
        />
      )}
    </PageShell>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number }) {
  return (
    <div className="card-lift rounded-2xl border border-border bg-card p-5">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <p className="mt-4 text-3xl font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function ApplicationDetailsModal({ app, onClose }: { app: Application; onClose: () => void }) {
  if (!app) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-[var(--shadow-elegant)] animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Application Details</h2>
          <button onClick={onClose} className="h-10 w-10 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Full Name</p>
              <p className="font-medium">{app.fullName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Application ID</p>
              <p className="font-medium">{app.id}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{app.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Phone</p>
              <p className="font-medium">{app.phone}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Age Range</p>
              <p className="font-medium">{app.ageRange}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Location</p>
              <p className="font-medium">{app.location}</p>
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground">Track</p>
            <p className="font-medium">{app.track}</p>
          </div>

          <div>
            <p className="text-sm text-muted-foreground">Background</p>
            <p className="font-medium">{app.background || 'Not provided'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="font-medium">{STATUS_LABELS[app.status]}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Submitted</p>
              <p className="font-medium">{app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground">Registration Code</p>
            <p className="font-medium">{app.registrationCode || 'Not generated'}</p>
          </div>

          {/* Document section */}
          <div className="border-t border-border pt-4">
            <p className="text-sm text-muted-foreground mb-2">Educational Document</p>
            {app.documentPath ? (
              <button
                onClick={() => window.open(`http://localhost:8000/api/uploads/documents/${app.documentPath}`, '_blank')}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Download className="h-4 w-4" />
                <span>Download Educational Result</span>
              </button>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>No document uploaded</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
